// Step 2 of the Optro work-step import wizard: pick the work steps to bring
// into Midship, grouped in accordions per audit selected on step 1. Each
// group lazy-loads its own server-paginated rows scoped by `ops_audit_id`
// (see WorkStepAuditGroup), so an instance with very many work steps only
// ever loads the audits under test. Already-imported rows show a badge and
// are not selectable, rather than being hidden.
import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { useOptroWorkStepSearch } from "../../../../hooks/useOptroImportWorkSteps";
import { SelectWorkStepsTable } from "./components/SelectWorkStepsTable";
import { useOptroWorkStepImportWizard } from "./optroWorkStepImportWizardContext";

// Product cap on work steps per import pass — each imported step becomes a
// workflow run (Optro fetch + possible AI generation), so batches stay
// bounded. Smaller than the backend's hard request cap on purpose.
const IMPORT_SELECTION_CAP = 100;

export const OptroWorkStepImportSelectPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");

  const {
    selectedAudits,
    selected,
    toggleWorkStep,
    isSelected,
    selectMany,
    deselectMany,
  } = useOptroWorkStepImportWizard();

  // One batched request per settled term across every selected audit —
  // inert while the search box is empty (browse mode).
  const searchActive = search.trim() !== "";
  const searchResults = useOptroWorkStepSearch(
    search,
    selectedAudits.map((audit) => audit.id),
  );

  // Deep-link protection: this step is meaningless without audits chosen on
  // step 1 (which also carries the Optro connection gate), so send direct
  // navigations back there.
  if (selectedAudits.length === 0) {
    return <Navigate to="/audit/work-steps/import/optro" replace />;
  }

  const auditCountLabel =
    selectedAudits.length === 1
      ? selectedAudits[0].name
      : `${selectedAudits.length} selected audits`;

  return (
    <div className="h-screen flex flex-col w-full p-6 max-w-[1200px] mx-auto">
      <nav className="text-sm text-stone-500 mb-4">
        <Link to="/audit/work-steps" className="hover:underline">
          Work Steps
        </Link>
        {" › "}
        <Link to="/audit/work-steps/import/optro" className="hover:underline">
          Import from OpsAudit
        </Link>
        {" › "}
        <span className="text-stone-900">Select Work Steps</span>
      </nav>
      <h1 className="text-2xl font-semibold">Select Work Steps to Import</h1>
      <p className="text-sm text-stone-500 mt-1 mb-4">
        Choose the work steps to bring into Midship for testing from{" "}
        {auditCountLabel}. Only work steps in Fieldwork sections are listed; the
        audits stay in Optro, which remains the system of record. You can import
        up to {IMPORT_SELECTION_CAP} work steps at a time.
      </p>

      <div className="flex items-center gap-3 mb-3 shrink-0">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by ID, title, audit, or preparer..."
            className="pl-9"
          />
        </div>
      </div>

      {/* The table is the page's only scroll region; the action bar below
          stays pinned while each audit group lazy-loads its rows. */}
      <div className="flex-1 min-h-0 border rounded-lg overflow-y-auto">
        <SelectWorkStepsTable
          audits={selectedAudits}
          searchMode={
            searchActive
              ? {
                  term: search.trim(),
                  buckets: searchResults.buckets,
                  loading: searchResults.loading,
                  error: searchResults.error,
                  retry: searchResults.retry,
                }
              : null
          }
          isSelected={isSelected}
          onToggle={toggleWorkStep}
          selectMany={selectMany}
          deselectMany={deselectMany}
        />
      </div>

      <div className="shrink-0 flex items-center justify-between pt-4">
        <span
          className={
            selected.length > IMPORT_SELECTION_CAP
              ? "text-sm text-red-600"
              : "text-sm text-stone-600"
          }
        >
          {selected.length} / {IMPORT_SELECTION_CAP} selected
          {selected.length > IMPORT_SELECTION_CAP &&
            " — remove some to continue"}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate("/audit/work-steps/import/optro")}
          >
            Back
          </Button>
          <Button
            disabled={
              selected.length === 0 || selected.length > IMPORT_SELECTION_CAP
            }
            onClick={() => navigate("/audit/work-steps/import/optro/review")}
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};
